/**
 * useMacro -- Macro Economics dashboard store (Zustand).
 */
import { create } from 'zustand'
import {
  getMacroDashboard,
  getMacroRates,
  getMacroInflation,
  getMacroLeadingIndicators,
  getMacroMonetaryPolicy,
  getMacroStreetSentiment,
  getMacroEconomicSurprise,
  getMacroHeatMap,
  getUKDashboard,
  getUKLeadingIndicators,
  getUKRates,
  getMacroGlobalRanking,
  getMacroG7,
  getMacroG20,
} from '@/lib/api'
import type {
  MacroDashboard,
  MacroRatesResponse,
  MacroInflationResponse,
  MacroLeadingIndicators,
  MonetaryPolicyResponse,
  StreetSentimentResponse,
  EconomicSurpriseResponse,
  MacroHeatMapResponse,
  UKEconomyDashboard,
  UKLeadingIndicatorsResponse,
  UKRatesResponse,
  GlobalRankingResponse,
  GroupSnapshotResponse,
} from '@/lib/types'

interface MacroState {
  // Dashboard
  dashboard: MacroDashboard | null
  dashboardLoading: boolean
  dashboardError: string | null
  fetchDashboard: () => Promise<void>

  // Rates
  rates: MacroRatesResponse | null
  ratesLoading: boolean
  ratesError: string | null
  fetchRates: () => Promise<void>

  // Inflation
  inflation: MacroInflationResponse | null
  inflationLoading: boolean
  inflationError: string | null
  fetchInflation: () => Promise<void>

  // Leading indicators
  leading: MacroLeadingIndicators | null
  leadingLoading: boolean
  leadingError: string | null
  fetchLeading: () => Promise<void>

  // Monetary policy
  monetaryPolicy: MonetaryPolicyResponse | null
  monetaryPolicyLoading: boolean
  monetaryPolicyError: string | null
  fetchMonetaryPolicy: () => Promise<void>

  // Street sentiment
  streetSentiment: StreetSentimentResponse | null
  streetSentimentLoading: boolean
  streetSentimentError: string | null
  fetchStreetSentiment: () => Promise<void>

  // Economic surprise
  economicSurprise: EconomicSurpriseResponse | null
  economicSurpriseLoading: boolean
  economicSurpriseError: string | null
  fetchEconomicSurprise: () => Promise<void>

  // Global heatmap
  heatMap: MacroHeatMapResponse | null
  heatMapLoading: boolean
  heatMapError: string | null
  fetchHeatMap: () => Promise<void>

  // UK Economy
  ukDashboard: UKEconomyDashboard | null
  ukDashboardLoading: boolean
  ukDashboardError: string | null
  fetchUKDashboard: () => Promise<void>

  ukLeading: UKLeadingIndicatorsResponse | null
  ukLeadingLoading: boolean
  ukLeadingError: string | null
  fetchUKLeading: () => Promise<void>

  ukRates: UKRatesResponse | null
  ukRatesLoading: boolean
  ukRatesError: string | null
  fetchUKRates: () => Promise<void>

  // Global Rankings (World Bank)
  globalRanking: GlobalRankingResponse | null
  globalRankingIndicator: string
  globalRankingLoading: boolean
  globalRankingError: string | null
  fetchGlobalRanking: (indicator: string) => Promise<void>

  // G7 snapshot
  g7: GroupSnapshotResponse | null
  g7Loading: boolean
  g7Error: string | null
  fetchG7: () => Promise<void>

  // G20 snapshot
  g20: GroupSnapshotResponse | null
  g20Loading: boolean
  g20Error: string | null
  fetchG20: () => Promise<void>

  // Active tab
  activeTab: string
  setActiveTab: (tab: string) => void
}

export const useMacro = create<MacroState>()((set) => ({
  // Dashboard
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
  fetchDashboard: async () => {
    set({ dashboardLoading: true, dashboardError: null })
    try {
      const data = await getMacroDashboard()
      set({ dashboard: data, dashboardLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch macro dashboard'
      set({ dashboardError: msg, dashboardLoading: false })
    }
  },

  // Rates
  rates: null,
  ratesLoading: false,
  ratesError: null,
  fetchRates: async () => {
    set({ ratesLoading: true, ratesError: null })
    try {
      const data = await getMacroRates()
      set({ rates: data, ratesLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch rates'
      set({ ratesError: msg, ratesLoading: false })
    }
  },

  // Inflation
  inflation: null,
  inflationLoading: false,
  inflationError: null,
  fetchInflation: async () => {
    set({ inflationLoading: true, inflationError: null })
    try {
      const data = await getMacroInflation()
      set({ inflation: data, inflationLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch inflation'
      set({ inflationError: msg, inflationLoading: false })
    }
  },

  // Leading indicators
  leading: null,
  leadingLoading: false,
  leadingError: null,
  fetchLeading: async () => {
    set({ leadingLoading: true, leadingError: null })
    try {
      const data = await getMacroLeadingIndicators()
      set({ leading: data, leadingLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch leading indicators'
      set({ leadingError: msg, leadingLoading: false })
    }
  },

  // Monetary policy
  monetaryPolicy: null,
  monetaryPolicyLoading: false,
  monetaryPolicyError: null,
  fetchMonetaryPolicy: async () => {
    set({ monetaryPolicyLoading: true, monetaryPolicyError: null })
    try {
      const data = await getMacroMonetaryPolicy()
      set({ monetaryPolicy: data, monetaryPolicyLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch monetary policy data'
      set({ monetaryPolicyError: msg, monetaryPolicyLoading: false })
    }
  },

  // Street sentiment
  streetSentiment: null,
  streetSentimentLoading: false,
  streetSentimentError: null,
  fetchStreetSentiment: async () => {
    set({ streetSentimentLoading: true, streetSentimentError: null })
    try {
      const data = await getMacroStreetSentiment()
      set({ streetSentiment: data, streetSentimentLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch street sentiment'
      set({ streetSentimentError: msg, streetSentimentLoading: false })
    }
  },

  // Economic surprise
  economicSurprise: null,
  economicSurpriseLoading: false,
  economicSurpriseError: null,
  fetchEconomicSurprise: async () => {
    set({ economicSurpriseLoading: true, economicSurpriseError: null })
    try {
      const data = await getMacroEconomicSurprise()
      set({ economicSurprise: data, economicSurpriseLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch economic surprise data'
      set({ economicSurpriseError: msg, economicSurpriseLoading: false })
    }
  },

  // Global heatmap
  heatMap: null,
  heatMapLoading: false,
  heatMapError: null,
  fetchHeatMap: async () => {
    set({ heatMapLoading: true, heatMapError: null })
    try {
      const data = await getMacroHeatMap()
      set({ heatMap: data, heatMapLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch global heatmap'
      set({ heatMapError: msg, heatMapLoading: false })
    }
  },

  // UK Economy
  ukDashboard: null,
  ukDashboardLoading: false,
  ukDashboardError: null,
  fetchUKDashboard: async () => {
    set({ ukDashboardLoading: true, ukDashboardError: null })
    try {
      const data = await getUKDashboard()
      set({ ukDashboard: data, ukDashboardLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch UK dashboard'
      set({ ukDashboardError: msg, ukDashboardLoading: false })
    }
  },

  ukLeading: null,
  ukLeadingLoading: false,
  ukLeadingError: null,
  fetchUKLeading: async () => {
    set({ ukLeadingLoading: true, ukLeadingError: null })
    try {
      const data = await getUKLeadingIndicators()
      set({ ukLeading: data, ukLeadingLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch UK leading indicators'
      set({ ukLeadingError: msg, ukLeadingLoading: false })
    }
  },

  ukRates: null,
  ukRatesLoading: false,
  ukRatesError: null,
  fetchUKRates: async () => {
    set({ ukRatesLoading: true, ukRatesError: null })
    try {
      const data = await getUKRates()
      set({ ukRates: data, ukRatesLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch UK rates'
      set({ ukRatesError: msg, ukRatesLoading: false })
    }
  },

  // Global Rankings (World Bank)
  globalRanking: null,
  globalRankingIndicator: 'gdp',
  globalRankingLoading: false,
  globalRankingError: null,
  fetchGlobalRanking: async (indicator: string) => {
    set({ globalRankingLoading: true, globalRankingError: null, globalRankingIndicator: indicator })
    try {
      const data = await getMacroGlobalRanking(indicator)
      set({ globalRanking: data, globalRankingLoading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch global ranking'
      set({ globalRankingError: msg, globalRankingLoading: false })
    }
  },

  // G7
  g7: null,
  g7Loading: false,
  g7Error: null,
  fetchG7: async () => {
    set({ g7Loading: true, g7Error: null })
    try {
      const data = await getMacroG7()
      set({ g7: data, g7Loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch G7 data'
      set({ g7Error: msg, g7Loading: false })
    }
  },

  // G20
  g20: null,
  g20Loading: false,
  g20Error: null,
  fetchG20: async () => {
    set({ g20Loading: true, g20Error: null })
    try {
      const data = await getMacroG20()
      set({ g20: data, g20Loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to fetch G20 data'
      set({ g20Error: msg, g20Loading: false })
    }
  },

  // Active tab
  activeTab: 'overview',
  setActiveTab: (tab) => set({ activeTab: tab }),
}))
